import React, { useMemo, useState } from "react";
import styled, { DefaultTheme } from "styled-components/native";
import { useTheme } from "styled-components/native";
import VDBText from "@components/atoms/Text";
import { Button, Spacer } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { useTranslation } from "react-i18next";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import LayoutContainer from "@components/layout/LayoutContainer";
import { getDaysLeft, getMessageState, menu } from "../MoreScreen/utils";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import { css } from "styled-components/native";
import ManagePlanPopUp from "./ManagePlan/ManagePlanPopUp";
import { Linking, Platform, TouchableOpacity } from "react-native";
import ContactUsModal from "@components/molecules/ContactUs";
import { messageType } from "@components/molecules/PopUp/types";
import PopUp from "@components/molecules/PopUp";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import PurchaseSeatsPopup from "./ManagePlan/PurchaseSeats";
import { useGlobalData } from "@redux/slices/globalSlice";
import { useGetUser } from "@apis/user";
import RenderHTML from "react-native-render-html";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";
import { useUserAnalytics } from "../../../hooks/useAnalytics";

type CardButton = {
  id: string;
  label: string;
  action: string;
  price_modes: number[];
};

type CardField = {
  id: string;
  label: string;
  value_key: string;
  show: boolean;
};

type OverviewCard = {
  id: string;
  title: string;
  price_modes: number[];
  fields: CardField[];
  buttons: CardButton[];
};

const OverviewScreen = ({
  setActiveId,
}: {
  setActiveId: (id: string) => void;
}) => {
  const { typography } = useScreenOptionsHooks();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const currentPlan = userInfo.user.plan_details?.plan;
  const planType = currentPlan?.name;
  const planExpiry = currentPlan?.expiry;
  const globalData = useGlobalData();
  const { daysLeftLabel, daysLeft } = getDaysLeft(planExpiry);
  const isExpired = daysLeft <= 0;
  const showError =
    daysLeft <=
    globalData?.subscription_config?.free_trial_banner?.error?.show_in_days
      ? true
      : false;
  const theme = useTheme();
  const priceMode = userInfo.user?.price_mode;
  const { trackButtonEvent, trackHyperLinkEvent } = useUserAnalytics() || {};
  const { cards } = useMemo(() => {
    const overview = (menu as any[]).find((i) => i.id === "overview");
    return { cards: (overview?.cards || []) as OverviewCard[] };
  }, []);
  const [isManagePlanPopUpOpen, setIsManagePlanPopUpOpen] = useState(false);
  const [showSheet, setShowSheet] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const { message, icon, color } = getMessageState(userInfo, theme, t);
  const [purchaseSeatsPopupVisible, setPurchaseSeatsPopupVisible] =
    useState(false);
  const [successResponseData, setSuccessResponseData] = useState<any>(null);
  const [showPurchaseSeatsSuccessPopup, setShowPurchaseSeatsSuccessPopup] =
    useState(false);
  const [userId, setUserId] = useState(userInfo.user.id);
  const {} = useGetUser(userId, setUserId);
  const isPurchaseSeatButtonVisible =
    userInfo.isPaidPlan() || userInfo.isUpcomingPaidPlan();
  const isPaymentPending = userInfo.isActiveFreeTrial()
    ? userInfo?.user?.plan_details?.upcoming_subscription?.is_payment_pending ||
      userInfo?.user?.plan_details?.upcoming_subscription
        ?.is_purchase_processing
    : userInfo?.user?.plan_details?.plan?.is_payment_pending ||
      userInfo?.user?.plan_details?.plan?.is_purchase_processing;
  const disableManagePlanButton = (b: CardButton) => {
    return (
      isPurchaseSeatButtonVisible &&
      isPaymentPending &&
      b.action === "manage_plan"
    );
  };
  const handleOpenPurchaseSeatsPopup = () => {
    setPurchaseSeatsPopupVisible(true);
  };
  const handleClosePurchaseSeatsPopup = () => {
    setPurchaseSeatsPopupVisible(false);
  };
  const handleCloseSuccessPopup = () => {
    setUserId(userInfo?.user?.id);
    setShowSuccessPopup(false);
    setIsManagePlanPopUpOpen(false);
  };
  const handleClosePurchaseSeatsSuccessPopup = () => {
    setSuccessResponseData(null);
    setUserId(userInfo?.user?.id);
    setShowPurchaseSeatsSuccessPopup(false);
  };
  const handleOpen = () => {
    setShowSheet(true);
  };
  const handleCloseSheet = () => {
    setShowSheet(false);
  };
  const getValue = (key: string) => {
    switch (key) {
      case "plan_ends_in":
        return isExpired ? "Ended" : daysLeftLabel;
      case "plan_name":
        return planType;
      case "paid_users":
        return userInfo.isUpcomingPaidPlan() && userInfo.isActiveFreeTrial()
          ? userInfo?.user?.plan_details?.upcoming_subscription?.paid_users || 0
          : userInfo.isActiveFreeTrial()
          ? "-"
          : currentPlan?.paid_users || "-";
      case "total_active_users":
        return currentPlan?.total_active_users || "-";
      case "used_seats":
        return currentPlan?.used_seats ?? "-";
      case "remaining_seats":
        return userInfo.user?.plan_details?.plan?.is_trial
          ? "-"
          : currentPlan?.remaining_seats;
      default:
        return "-";
    }
  };

  const onButtonAction = (action: string) => {
    switch (action) {
      case "manage_plan":
        if (
          (userInfo.isActiveFreeTrial() &&
            userInfo.user.plan_details.upcoming_subscription === null) ||
          isExpired ||
          userInfo.isUpcomingNotPaidPlan()
        ) {
          trackButtonEvent(
            EventName.BUTTON_CLICK,
            EventType.MANAGE_PLAN_BUTTON_CLICK,
            { component_type: ViewType.SCREEN_VIEW },
            ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
          );
          setIsManagePlanPopUpOpen(true);
        } else {
          trackButtonEvent(
            EventName.BUTTON_CLICK,
            EventType.PURCHASE_SEATS_BUTTON_CLICK,
            { component_type: ViewType.SCREEN_VIEW },
            ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
          );
          handleOpenPurchaseSeatsPopup();
        }
        break;
      case "manage_seats":
        trackButtonEvent(
          EventName.BUTTON_CLICK,
          EventType.USER_SEATS_BUTTON_CLICK,
          { component_type: ViewType.SCREEN_VIEW },
          ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
        );
        if (theme.isLargeScreen) {
          setActiveId("users_seats");
        } else {
          navigate(Routes.AccountOverviewUsersSeats);
        }
        break;
      default:
        break;
    }
  };

  const getbuttonText = () => {
    if (
      ((userInfo.isActiveFreeTrial() &&
        userInfo.user.plan_details.upcoming_subscription === null) ||
        isExpired) &&
      !userInfo.isUpcomingNotPaidPlan()
    ) {
      return t("managePlan.card.button.managePlan.text", "Manage Plan");
    } else if (userInfo.isUpcomingNotPaidPlan()) {
      return t("managePlan.card.button.modifyPlan.text", "Modify Plan");
    } else {
      return t("managePlan.card.button.purchaseSeats.text", "Purchase Users");
    }
  };

  return (
    <LayoutContainer>
      <CardsGrid>
        {cards
          .filter((c) => c.price_modes?.includes(priceMode))
          .map((card) => (
            <Card key={card.id}>
              <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
                {t(card.title)}
              </VDBText>
              <Spacer size={SpaceType.Big} />
              <CardBody>
                {(() => {
                  const visibleFields = card.fields.filter((f) => f.show);
                  const columns: CardField[][] = [];
                  for (let i = 0; i < visibleFields.length; i += 2) {
                    columns.push(visibleFields.slice(i, i + 2));
                  }
                  return columns.map((column, columnIndex) => (
                    <CardColumn key={`${card.id}-col-${columnIndex}`}>
                      {column.map((f) => (
                        <FieldRow key={f.id}>
                          <KeyContainer>
                            <VDBText
                              type={typography["Body-R"]}
                              color={theme["core-06"]}
                            >
                              {t(f.label)}
                            </VDBText>
                          </KeyContainer>
                          <VDBText
                            type={typography["Body-M"]}
                            color={
                              (isExpired || showError) &&
                              f.value_key === "plan_ends_in"
                                ? theme["error-01"]
                                : theme["core-05"]
                            }
                          >
                            {getValue(f.value_key)}
                          </VDBText>
                        </FieldRow>
                      ))}
                      {columnIndex === 0 &&
                        card.id === "current_plan" &&
                        message &&
                        message !== "" && (
                          <>
                            <Spacer size={SpaceType.Basic} />
                            <ErrorContainer>
                              {icon && (
                                <VDBIcon
                                  name={icon}
                                  color={color}
                                  size={24}
                                  variant={IconsVariant.VDBCustom}
                                />
                              )}
                              <TextContainer>
                                <RenderHTML
                                  source={{
                                    html: t(message),
                                  }}
                                  renderersProps={{
                                    a: {
                                      onPress: (_event, href) => {
                                        if (href) {
                                          trackHyperLinkEvent(
                                            href,
                                            ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
                                          );
                                          Linking.openURL(href);
                                        }
                                      },
                                    },
                                  }}
                                  tagsStyles={{
                                    body: {
                                      ...typography["Minor-M"],
                                      color: color,
                                    },
                                    a: {
                                      color: theme["link-static"],
                                      textDecorationLine: "none",
                                    },
                                  }}
                                />
                              </TextContainer>
                            </ErrorContainer>
                          </>
                        )}
                      {columnIndex === 0 &&
                        card.id === "team_seats" &&
                        message &&
                        message !== "" &&
                        theme.isLargeScreen && (
                          <Spacer size={SpaceType.ExtraLarge} />
                        )}
                    </CardColumn>
                  ));
                })()}
              </CardBody>
              <CardFooter>
                {card.buttons
                  .filter((b) => {
                    if (b.action === "manage_plan" && Platform.OS !== "web")
                      return false;
                    return b.price_modes?.includes(priceMode) ?? true;
                  })
                  .slice(0, Platform.OS === "web" ? 1 : 2)
                  .map((b) => (
                    <ButtonContainer key={b.id}>
                      <Button
                        type={ButtonType.primary}
                        title={
                          b.action === "manage_plan"
                            ? getbuttonText()
                            : t(b.label)
                        }
                        category={ButtonCategory.default}
                        onPress={() => onButtonAction(b.action)}
                        disabled={disableManagePlanButton(b)}
                      />
                    </ButtonContainer>
                  ))}
                {Platform.OS !== "web" && card.id === "current_plan" && (
                  <>
                    <Spacer size={SpaceType.Basic} />
                    <VDBText
                      type={typography["Minor-R"]}
                      color={theme["core-06"]}
                    >
                      {t(
                        "managePlan.mobile.subscription.text",
                        "To manage or change your plan, please visit the web version of VDB Marketplace"
                      )}
                    </VDBText>
                  </>
                )}
              </CardFooter>
            </Card>
          ))}
        {Platform.OS !== "web" && (
          <NeedHelpContainer>
            <VDBText color={theme["core-06"]} type={typography["Body-R"]}>
              {t("global.text.help")}
            </VDBText>
            <Spacer size={SpaceType.Basic} />
            <TouchableOpacity onPress={handleOpen}>
              <VDBText color={theme["link-static"]} type={typography["Body-M"]}>
                {t("global.text.contactUs")}
              </VDBText>
            </TouchableOpacity>
            <ContactUsModal
              isOpen={showSheet}
              onClose={handleCloseSheet}
              screenName={"Account Overview"}
            />
          </NeedHelpContainer>
        )}
      </CardsGrid>
      <ManagePlanPopUp
        isOpen={isManagePlanPopUpOpen}
        onClose={() => setIsManagePlanPopUpOpen(false)}
        setShowSuccessPopup={setShowSuccessPopup}
      />
      <PurchaseSeatsPopup
        isOpen={purchaseSeatsPopupVisible}
        onClose={handleClosePurchaseSeatsPopup}
        setShowSuccessPopup={setShowPurchaseSeatsSuccessPopup}
        setSuccessResponseData={setSuccessResponseData}
      />
      {showSuccessPopup && (
        <PopUp
          type={messageType.Success}
          title={t("managePlan.popUp.successPopUp.title.text", "Thank you!")}
          buttons={[
            {
              type: ButtonType.primary,
              title: "Ok",
              onPressAction: handleCloseSuccessPopup,
            },
          ]}
          HtmlSource={{
            html: t(
              "managePlan.popUp.successPopUp.description.text",
              `<div>We've sent the <strong style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 800; line-height: 20px;">invoice and payment</strong> link to your email.
                It may take a few minutes to arrive.</div>
                <div style="height: 12px;"></div>
                <div>If you don't receive it within 10 minutes, please contact <a href="mailto:billing@vdbapp.com" target="_blank" style="color: #007AFF; text-decoration: none;">billing@vdbapp.com</a> for assistance.</div>`
            ),
          }}
          styleKey="Body-R"
          colorKey="core-05"
        />
      )}
      {showPurchaseSeatsSuccessPopup && (
        <PopUp
          type={messageType.Success}
          title={t("managePlan.popUp.successPopUp.title.text", "Thank you!")}
          buttons={[
            {
              type: ButtonType.primary,
              title: t("managePlan.popUp.successPopUp.button.title.text", "Ok"),
              onPressAction: handleClosePurchaseSeatsSuccessPopup,
            },
            {
              type: ButtonType.secondary,
              title: t(
                "managePlan.popUp.successPopUp.button.addMore.text",
                "Add More Users"
              ),
              onPressAction: () => {
                trackButtonEvent(
                  EventName.BUTTON_CLICK,
                  EventType.ADD_MORE_USERS_BUTTON_CLICK,
                  { component_type: ViewType.SCREEN_VIEW },
                  ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
                );
                setPurchaseSeatsPopupVisible(true);
                setShowPurchaseSeatsSuccessPopup(false);
              },
            },
          ]}
          HtmlSource={{
            html: t(successResponseData?.message),
          }}
          styleKey="Body-R"
          colorKey="core-05"
        />
      )}
    </LayoutContainer>
  );
};

const CardsGrid = styled.View`
  padding: ${({ theme }) =>
    !theme.isLargeScreen ? SpaceType.Double : SpaceType.ExtraLarge}px;
  gap: ${SpaceType.Double}px;
  flex-direction: ${({ theme }) => (!theme.isLargeScreen ? "column" : "row")};
  ${({ theme }) =>
    theme.isLargeScreen &&
    css`
      flex-wrap: wrap;
    `}
`;

const Card = styled.View`
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  padding: ${SpaceType.Double}px;
  ${({ theme }) =>
    theme.isLargeScreen &&
    css`
      flex: 1;
    `}
`;

const CardBody = styled.View`
  flex-direction: row;
  gap: ${SpaceType.Big}px;
`;

const CardColumn = styled.View`
  flex: 1;
  gap: ${SpaceType.Basic}px;
`;

const FieldRow = styled.View`
  flex-direction: row;
`;

const KeyContainer = styled.View`
  width: ${({ theme }) => (theme.isMobile ? "120px" : "130px")};
`;

const ButtonContainer = styled.View`
  margin-top: ${SpaceType.Double}px;
  max-width: 150px;
`;

const NeedHelpContainer = styled.View`
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const ErrorContainer = styled.View`
  flex-direction: row;
  gap: ${SpaceType.Basic}px;
  flex-wrap: wrap;
  align-items: center;
`;

const TextContainer = styled.View`
  flex: 1;
`;
const CardFooter = styled.View`
  margin-top: auto;
`;
export default OverviewScreen;
